// src/components/AudioPlayer.tsx - Update the download section
import React, { useState, useEffect, useRef } from "react";
import { Button, Slider, Alert, Tag, Tooltip, Progress, message } from "antd";
import { PlayCircleOutlined, PauseCircleOutlined, StepBackwardOutlined, StepForwardOutlined, RedoOutlined, CloudDownloadOutlined, SoundOutlined } from "@ant-design/icons";
import { ReportFile } from "../types/file/FileTypes";
import { useAudioStream } from "../hooks/consult/useAudioStream";
import { getPlaybackPosition, updatePlaybackPosition, resetPlaybackPosition } from "../api/file/playbackPositionApi";
import { AudioCacheManager } from "../services/AudioCacheManager";
import type { UserUuid } from "../types/identity";

interface AudioPlayerProps {
	report: ReportFile;
	patientId: UserUuid;
	onDownload: () => void;
	downloading?: boolean;
	onError: (message: string) => void;
	onSuccess: (message: string) => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ report, patientId, onDownload, downloading, onError, onSuccess }) => {
	const audioRef = useRef<HTMLAudioElement>(null);
	const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastSavedPositionRef = useRef<number>(0);

	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [savedPosition, setSavedPosition] = useState<number>(0);
	const [positionRestored, setPositionRestored] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);

	const {
		audioUrl,
		loading,
		progress,
		error: streamError,
		cached,
		initialize,
	} = useAudioStream({
		patientId,
		fileId: report.id,
		autoStart: true,
		onError,
		onSuccess,
	});

	useEffect(() => {
		const loadPosition = async () => {
			try {
				const positionData = await getPlaybackPosition(patientId, report.id);
				setSavedPosition(positionData.position);
			} catch (error) {
				console.error("Failed to load playback position:", error);
			}
		};
		loadPosition();
	}, [patientId, report.id]);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio || !audioUrl) return;

		const updateTime = () => {
			setCurrentTime(audio.currentTime);
			scheduleSavePosition(audio.currentTime);
		};

		const updateDuration = () => {
			setDuration(audio.duration);
		};

		const handleEnded = () => {
			setIsPlaying(false);
			savePositionNow(0);
		};

		const handleError = (e: Event) => {
			const audio = e.target as HTMLAudioElement;
			const error = audio.error;
			console.error("Audio playback error:", error);
			if (error) {
				onError(`Audio playback failed: ${error.message || "Unknown error"}`);
			}
		};

		audio.addEventListener("timeupdate", updateTime);
		audio.addEventListener("loadedmetadata", updateDuration);
		audio.addEventListener("ended", handleEnded);
		audio.addEventListener("error", handleError);

		return () => {
			audio.removeEventListener("timeupdate", updateTime);
			audio.removeEventListener("loadedmetadata", updateDuration);
			audio.removeEventListener("ended", handleEnded);
			audio.removeEventListener("error", handleError);
		};
	}, [audioUrl, onError]);

	useEffect(() => {
		if (!audioUrl || !audioRef.current || positionRestored || savedPosition <= 0) return;

		const audio = audioRef.current;

		const restorePosition = () => {
			if (audio.readyState >= 2 && duration > 0) {
				audio.currentTime = Math.min(savedPosition, duration);
				setCurrentTime(savedPosition);
				setPositionRestored(true);
			}
		};

		if (audio.readyState >= 2) {
			restorePosition();
		} else {
			audio.addEventListener("loadedmetadata", restorePosition);
			return () => audio.removeEventListener("loadedmetadata", restorePosition);
		}
	}, [audioUrl, savedPosition, positionRestored, duration]);

	useEffect(() => {
		return () => {
			if (saveTimerRef.current) {
				clearTimeout(saveTimerRef.current);
			}
			if (currentTime > 0) {
				savePositionNow(currentTime);
			}
		};
	}, [currentTime]);

	const scheduleSavePosition = (position: number) => {
		if (Math.abs(position - lastSavedPositionRef.current) < 5) return;

		if (saveTimerRef.current) {
			clearTimeout(saveTimerRef.current);
		}

		saveTimerRef.current = setTimeout(() => {
			savePositionNow(position);
		}, 2000);
	};

	const savePositionNow = async (position: number) => {
		try {
			await updatePlaybackPosition(patientId, report.id, position);
			lastSavedPositionRef.current = position;
		} catch (error) {
			console.error("Failed to save playback position:", error);
		}
	};

	const handleDownloadClick = async () => {
		setIsDownloading(true);

		try {
			const cacheManager = AudioCacheManager.getInstance();
			await cacheManager.initialize();

			const cachedEntry = await cacheManager.get(report.id);

			if (cachedEntry) {
				onSuccess("Downloading from cache...");
				const url = URL.createObjectURL(cachedEntry.blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = report.file_name.replace(/\.[^/.]+$/, ".mp3");
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
				onSuccess("Audio file downloaded successfully");
			} else {
				message.info({
					content: "Processing audio file... This may take up to 30 seconds.",
					duration: 5,
					key: "audio-processing",
				});
				onDownload();
			}
		} catch (error) {
			console.error("Download failed:", error);
			onError("Failed to download audio file");
		} finally {
			setIsDownloading(false);
		}
	};

	const togglePlayPause = () => {
		if (!audioRef.current || !audioUrl) return;

		if (isPlaying) {
			audioRef.current.pause();
			setIsPlaying(false);
		} else {
			audioRef.current.play().catch((err) => {
				console.error("Playback failed:", err);
				onError("Failed to play audio");
			});
			setIsPlaying(true);
		}
	};

	const handleSeek = (value: number) => {
		if (!audioRef.current) return;
		audioRef.current.currentTime = value;
		setCurrentTime(value);
	};

	const skip = (seconds: number) => {
		if (!audioRef.current) return;
		const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
		handleSeek(newTime);
	};

	const handleResetPosition = async () => {
		try {
			await resetPlaybackPosition(patientId, report.id);
			if (audioRef.current) {
				audioRef.current.currentTime = 0;
				setCurrentTime(0);
			}
			setSavedPosition(0);
			lastSavedPositionRef.current = 0;
			setPositionRestored(true);
			onSuccess("Playback position reset");
		} catch (error) {
			onError("Failed to reset position");
		}
	};

	const formatTime = (seconds: number) => {
		if (!isFinite(seconds)) return "0:00";
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	};

	if (loading) {
		return (
			<div className="flex flex-col items-center justify-center p-4 sm:p-8" style={{ minHeight: "60vh" }}>
				<SoundOutlined style={{ fontSize: 48, color: "#1890ff", marginBottom: 16 }} />
				<Progress percent={progress} status="active" style={{ width: "100%", maxWidth: 400 }} />
				<p className="mt-4 text-sm text-gray-600">{cached ? "Loading from cache..." : "Downloading audio file..."}</p>
				{!cached && progress > 0 && <p className="mt-2 text-xs text-gray-500">Audio is being processed on the server for optimal playback</p>}
			</div>
		);
	}

	if (streamError) {
		return (
			<div className="flex flex-col items-center justify-center p-4 sm:p-8" style={{ minHeight: "60vh" }}>
				<Alert
					message="Error"
					description={streamError}
					type="error"
					showIcon
					action={
						<Button size="small" onClick={initialize}>
							Retry
						</Button>
					}
					className="w-full max-w-md"
				/>
			</div>
		);
	}

	return (
		<div data-testid="files-audio-player" className="flex flex-col items-center justify-center p-4 sm:p-8" style={{ minHeight: "60vh" }}>
			<audio ref={audioRef} src={audioUrl || undefined} preload="metadata" />

			<div className="w-full max-w-md sm:max-w-2xl px-4">
				<div className="flex justify-center mb-6 sm:mb-8">
					<div className="rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-xl" style={{ width: 120, height: 120 }}>
						<SoundOutlined style={{ fontSize: 48, color: "white" }} />
					</div>
				</div>

				<div className="text-center mb-4 sm:mb-6">
					<h3 className="text-lg sm:text-xl font-semibold mb-2 px-2 break-words">{report.file_name}</h3>
					<div className="flex flex-wrap justify-center gap-1">
						<Tag color="purple" className="text-xs">
							Audio Report
						</Tag>
						{cached && (
							<Tag color="green" className="text-xs">
								Cached
							</Tag>
						)}
						{savedPosition > 0 && (
							<Tag color="blue" className="text-xs">
								Resume from {formatTime(savedPosition)}
							</Tag>
						)}
					</div>
				</div>

				<div className="mb-4 sm:mb-6 px-2">
					<Slider value={currentTime} max={duration || 100} onChange={handleSeek} tooltip={{ formatter: (value) => formatTime(value || 0) }} className="mb-2" disabled={!audioUrl} />
					<div className="flex justify-between text-xs sm:text-sm px-1">
						<span className="text-gray-600">{formatTime(currentTime)}</span>
						<span className="text-gray-600">{formatTime(duration)}</span>
					</div>
				</div>

				<div className="flex items-center justify-center gap-2 sm:gap-4 mb-4 sm:mb-6 px-2">
					<Tooltip title="Restart">
						<Button type="text" size="small" icon={<RedoOutlined />} onClick={handleResetPosition} disabled={!audioUrl} className="hidden sm:flex">
							Restart
						</Button>
						<Button type="text" size="small" icon={<RedoOutlined />} onClick={handleResetPosition} disabled={!audioUrl} className="sm:hidden" />
					</Tooltip>

					<Tooltip title="-10s">
						<Button type="text" size="small" icon={<StepBackwardOutlined />} onClick={() => skip(-10)} disabled={!audioUrl} className="hidden sm:flex">
							10s
						</Button>
						<Button type="text" size="small" icon={<StepBackwardOutlined />} onClick={() => skip(-10)} disabled={!audioUrl} className="sm:hidden" />
					</Tooltip>

					<Tooltip title={isPlaying ? "Pause" : "Play"}>
						<Button
							type="primary"
							shape="circle"
							size="large"
							icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
							onClick={togglePlayPause}
							disabled={!audioUrl}
							style={{ width: 48, height: 48, fontSize: 24 }}
							className="sm:!w-16 sm:!h-16 sm:text-3xl"
						/>
					</Tooltip>

					<Tooltip title="+10s">
						<Button type="text" size="small" icon={<StepForwardOutlined />} onClick={() => skip(10)} disabled={!audioUrl} className="hidden sm:flex">
							10s
						</Button>
						<Button type="text" size="small" icon={<StepForwardOutlined />} onClick={() => skip(10)} disabled={!audioUrl} className="sm:hidden" />
					</Tooltip>

					<Tooltip title="Download">
						<Button type="text" size="small" icon={<CloudDownloadOutlined />} onClick={handleDownloadClick} loading={isDownloading || downloading} disabled={!audioUrl} className="hidden sm:flex">
							Download
						</Button>
						<Button type="text" size="small" icon={<CloudDownloadOutlined />} onClick={handleDownloadClick} loading={isDownloading || downloading} disabled={!audioUrl} className="sm:hidden" />
					</Tooltip>
				</div>
			</div>
		</div>
	);
};

export default AudioPlayer;
